import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface MachinationHistoryPageProps {
}

const MachinationHistoryPage = (props: MachinationHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_post_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Recovery Age - Chase the Fox`,
        description: "The Recovery Age of Casarok is the time in which the people of Casarok needed to recover from the events of the Great Cataclysm",
        keywords: ["casarok", "casarok history", "recovery age", "fantasy timeline", "fantasy world history", "dark age fantasy", "fantasy dark age"],
        imgSrc: _map_src,
        url: `world/history/recovery-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Post-Cataclysm Map'
                         alt="A map of Casarok after the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Recovery Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_post_cataclysm.png`)}
                             title='Casarok Post-Cataclysm Map'
                             alt="A map of Casarok after the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years 5 - 500 ACE.</em>
                    </p>
                    <p>
                        The Recovery Age of Casarok covers the time in which the world made an effort to heal from the
                        events of <NavLink className="world-link" to='/world/history/cataclysmic-age'>the Great Cataclysm</NavLink>. A
                        huge portion of the anthros in the world were killed when the worldwide quake split the
                        continent into several. Many countries were displaced and climates changed drastically in
                        others. Few nations remained that had the capability to rule over its people. The anthros of
                        the world needed to pick up the pieces and rebuild. They started with small colonies that
                        survived through the will of mages to protect the buildings and peoples there. Many near the
                        site of the end of the War of the Gods looked to their
                        hero, <NavLink className="world-link" to="/characters/pyrrhe">Pyrrhe Drake</NavLink>, for
                        leadership. Pyrrhe refused at first, refusing the role of king, but he eventually became the
                        leader that they needed.
                    </p>
                    <p>
                        The <NavLink className="world-link" to="/world/religion/pantheon">gods and goddesses</NavLink> who
                        remained with Casarok mostly left it behind. Mother, the goddess who led the winning side of the
                        War of the Gods, made the decree that they leave Casarok to the mortals. They mostly agreed and
                        decided to watch from afar. However, the god of magic, Hazado disagreed with that sentiment.
                        Wanting to right his previous wrongs, he helped the rebuilding of Casarok by providing them
                        with powerful magic. Ptahlemy, the god of knowledge who stood beside Mother as her closest
                        advisor in the war, found out about Hazado's efforts and tried to convince him to stop. Ptahlemy
                        felt that the anthros needed to build their own future through technological advancements of
                        their own creation. Magic was reminiscent of the gods' intervention. The two of them dueled to
                        settle their conflicting ideologies. Ptahlemy came out on top, and Hazado was never heard from
                        again.
                    </p>
                    <p>
                        <em>How does Casarok's Recovery Age differ from the dark ages of Earth?</em><br/>
                        Every single empire in the world had already fallen. While Europe had the divided Roman empire
                        to rebuild from, the people of Casarok had far less to work with. The fact that many fewer
                        people were also left in the world was also a difficult factor to consider. The Black Plague on
                        Earth would not come for centuries after their dark ages, but the event that wiped out so many
                        Casarokans had already occurred. Magic was heavily used to help in the rebuilding efforts.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>5 ACE</em>: Mother and Ptahlemy decree to the other gods and goddesses that none of them
                            should meddle in the affairs of Casarok any further.
                        </li>
                        <li>
                            <em>6 ACE</em>: Pyrrhe Drake becomes the king of a new nation in Centralia, Empyrrhia,
                            after declining the role for several months.
                        </li>
                        <li>
                            <em>19 ACE</em>: Hazado breaks from the pact the pantheon made and begins sharing powerful
                            magic with the anthros to help them rebuild.
                        </li>
                        <li>
                            <em>106 ACE</em>: Pyrrhe Drake passes away of old age.
                        </li>
                        <li>
                            <em>235 ACE</em>: The
                            oldest <NavLink className="world-link" to="/world/history/modern-age">modern</NavLink> nation
                            of Terasama is formed.
                        </li>
                        <li>
                            <em>281 ACE</em>: After many years of mismanagement by Pyrrhe's successors, Empyrrhia falls
                            at the hands of surrounding states that had formed from the duchies of the old Leonida
                            Kingdom.
                        </li>
                        <li>
                            <em>360 ACE</em>: The Frostfire Road is established in the Frostland, opening up trade and
                            resource sharing among the various settlements there.
                        </li>
                        <li>
                            <em>456 ACE</em>: Ptahlemy discovers Hazado's chicanery and orders him to stop sharing
                            magic with the anthros.
                        </li>
                        <li>
                            <em>471 ACE</em>: Ptahlemy finds that Hazado has not ceased his efforts to meddle with the
                            anthros. They duel to determine whether magic or technology would guide the fate of the
                            world. Ptahlemy wins the duel, and Hazado is never heard from again.
                        </li>
                        <li>
                            <em>494 ACE</em>: The castle and mage tower in Merlemot of modern day Erthuria are rebuilt,
                            inspiring many other settlements in the world to build similar defenses.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default MachinationHistoryPage;